// When debug test is enabled:
// * We lose context the first time we call retryOnLostContext. This should
// let PC / other GL calls to throw lost context when doing certain operations.
// * We force set a timer to wait x (ms) before calling restore context. This
// simulates when gl context is restored after a lost context.
//
// Then we wait to see if our rendering resources do in fact restore themselves
// to the proper state.
const DEBUG_TEST_LOSE_CONTEXT = false;
const DEBUG_TEST_RESTORE_MS = 2000;
const DEBUG_TEST_LOG_LOSE_CONTEXT = false;

const retryOnLostContextErrorMessageConditions = [
  "fenceSync failed",
  "clientWaitSync failed",
  "Cannot destructure property 'maxSamples' of 'this._device' as it is undefined.",
] as const;

type LostContextError = Error & { cause: { lostContext: boolean } };
function isLostContextError(error: unknown): error is LostContextError {
  return error instanceof Error && "cause" in error && "lostContext" in (error.cause as { lostContext: boolean });
}

/**
 * The callback function to execute with each attempt during a lost context.
 * @param context The context object.
 * @returns A promise with the return type of our callback.
 */
export type RetryOnLostContextCallback<T> = (context: { attempt: number }) => Promise<T>;

export function errorCausedByLostContext(error: unknown): boolean {
  const glDidFailFromLostContext =
    error instanceof Error &&
    (retryOnLostContextErrorMessageConditions.some((message) => error.message.includes(message)) ||
      (isLostContextError(error) && error.cause.lostContext));

  // Check if the context is lost right now.
  const isContextLost = window.app.graphicsDevice.gl.isContextLost();

  return glDidFailFromLostContext || isContextLost;
}

/**
 * Retry if callback fails with a lost context.
 * @param callback The function to execute.
 * @param numRetries If callback fails, how many times to retry (default 3).
 * @returns A promise with the return type of our callback.
 */
export async function retryOnLostContext<T>(callback: RetryOnLostContextCallback<T>, numRetries = 3): Promise<T> {
  const tryCount = numRetries + 1;
  let attempt = 1;
  let lastError: unknown;
  while (attempt <= tryCount) {
    try {
      if (DEBUG_TEST_LOSE_CONTEXT && attempt === 1) {
        _glLoseContext();
      }

      return await callback({ attempt });
    } catch (err: unknown) {
      if (errorCausedByLostContext(err)) {
        if (DEBUG_TEST_LOSE_CONTEXT) {
          if (DEBUG_TEST_LOG_LOSE_CONTEXT) {
            console.log(`[Test] Restoring WebGL in ${DEBUG_TEST_RESTORE_MS} ms.`);
          }
          setTimeout(() => {
            _glRestoreContext();
          }, DEBUG_TEST_RESTORE_MS);
        }

        attempt = attempt + 1;
        lastError = err;
        await ensureValidWebGLContext();
      } else {
        throw err;
      }
    }
  }

  return Promise.reject(lastError);
}

let _glExtLoseCtx: WEBGL_lose_context | null;
function ensureGlExtLoseCtx(loseCtxExt: WEBGL_lose_context | null): loseCtxExt is WEBGL_lose_context {
  if (!loseCtxExt) {
    loseCtxExt = window.app.graphicsDevice.gl.getExtension("WEBGL_lose_context");
  }
  // Update singleton.
  _glExtLoseCtx = loseCtxExt;
  return loseCtxExt != null;
}
function _glLoseContext() {
  if (DEBUG_TEST_LOG_LOSE_CONTEXT) {
    console.log("[Test] WebGL context was lost.");
  }
  if (ensureGlExtLoseCtx(_glExtLoseCtx)) {
    _glExtLoseCtx.loseContext();
  }
}
function _glRestoreContext() {
  if (DEBUG_TEST_LOG_LOSE_CONTEXT) {
    console.log("[Test] WebGL context was restored.");
  }
  if (ensureGlExtLoseCtx(_glExtLoseCtx)) {
    _glExtLoseCtx.restoreContext();
  }
}

/**
 * Block until gl context is restored.
 * @returns A void promise.
 */
async function ensureValidWebGLContext() {
  if (window.app.graphicsDevice.gl.isContextLost()) {
    if (DEBUG_TEST_LOG_LOSE_CONTEXT) {
      console.log("Waiting for WebGL context restored.");
    }
    const canvas = window.app.graphicsDevice.canvas;
    return new Promise<void>((resolve, _reject) => {
      function handleWebGLRestoreContext() {
        canvas.removeEventListener("webglcontextrestored", handleWebGLRestoreContext, false);
        resolve();
      }
      canvas.addEventListener("webglcontextrestored", handleWebGLRestoreContext, false);
    });
  }
}
